import React, { useMemo, useState } from 'react';
import { SubjectTitle } from './NightRow';
import { useI18n } from '../../contexts/I18nContext';
import { fmtInTz } from '../../lib/tz';
import { NightBar, ResponsiveTicks } from '../movement/NightBar';
import { VerdictDot, VERDICT_NAME, nightSentence } from '../movement/nightVerdict';

// 7 night bars stacked like a sleep diary (oldest on top); tap a night to read its sentence.
export function WeekTrendRow({ subject, nights, buckets }) {
  const { t, lang } = useI18n();
  const [sel, setSel] = useState(0);
  const ordered = useMemo(() => (nights ? [...nights].reverse() : null), [nights]);
  const { id } = subject;

  return (
    <div className="bg-white border border-stone_line rounded-2xl shadow-soft p-5 sm:p-6" data-testid={`week-row-${id}`}>
      <div className="mb-4"><SubjectTitle subject={subject} testid={`week-row-link-${id}`} /></div>
      {!ordered ? <div className="h-40 rounded-md bg-bg-alt animate-pulse" /> : (
        <>
          <ul className="space-y-1.5" data-testid={`week-chart-${id}`}>
            {ordered.map((n) => {
              const tz = n.window.tz;
              const active = n.offset === sel;
              return (
                <li key={n.offset}>
                  <button type="button" onClick={() => setSel(n.offset)} aria-pressed={active} data-testid={`week-night-${id}-${n.offset}`} data-kind={n.kind}
                    className={`w-full flex items-center gap-2 sm:gap-3 rounded-lg px-1.5 py-1 transition-colors ${active ? 'bg-bg-alt' : 'hover:bg-bg-alt/60'}`}>
                    <span className={`w-12 sm:w-16 shrink-0 text-left text-[11px] ${active ? 'text-ink font-semibold' : 'text-ink-soft'}`}>
                      {fmtInTz(n.window.fromMs, tz, { weekday: 'short', day: 'numeric' }, lang)}
                    </span>
                    <VerdictDot kind={n.kind} size="sm" />
                    <div className="flex-1 min-w-0"><NightBar buckets={buckets} ids={subject.ids} win={n.window} height="h-5" ticks={false} testid={`week-bar-${id}-${n.offset}`} /></div>
                    <span className="hidden sm:block w-40 shrink-0 text-left text-[11px] text-ink-soft truncate">{t(VERDICT_NAME[n.kind])}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center gap-2 sm:gap-3 px-1.5">
            <span className="w-12 sm:w-16 shrink-0" /><span className="w-6 shrink-0" />
            <div className="flex-1 min-w-0"><ResponsiveTicks win={ordered[ordered.length - 1].window} /></div>
            <span className="hidden sm:block w-40 shrink-0" />
          </div>
          {(() => {
            const n = nights.find((x) => x.offset === sel);
            const hm = (ms) => fmtInTz(ms, n.window.tz, { hour: '2-digit', minute: '2-digit' }, lang);
            return (
              <p className="text-sm text-ink mt-3 leading-relaxed" data-testid={`week-sentence-${id}`}>
                <span className="font-semibold">{sel === 0 ? t('mvn_last_night') : fmtInTz(n.window.fromMs, n.window.tz, { weekday: 'long', day: 'numeric', month: 'short' }, lang)}</span>
                <span className="text-ink-muted"> · {t(VERDICT_NAME[n.kind])}</span>
                <br />{nightSentence(t, n, hm)}
              </p>
            );
          })()}
        </>
      )}
    </div>
  );
}
